import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Flame, BadgeCheck, Star, Pencil, Crown, User, Eye, Lock, Users, UserCheck, Link2, LayoutTemplate, HardDrive } from 'lucide-react'
import type { CategoryStyle } from '../lib/categoryColors'
import { formatBytes } from '../lib/format'
import { VisitorBadge } from './VisitorBadge'
const statusConfig: Record<string, { color: string; label: string; pulse: boolean }> = {
  running: { color: 'bg-green-500', label: 'Live', pulse: false },
  stopped: { color: 'bg-gray-400', label: 'Stopped', pulse: false },
  starting: { color: 'bg-yellow-500', label: 'Starting', pulse: true },
  error: { color: 'bg-red-500', label: 'Error', pulse: false },
}

export type TopRowVariant = 'url' | 'status-badge' | 'creator'

export interface BrowseCardApp {
  name: string
  display_name: string | null
  description: string | null
  status: string
  url: string
  is_featured?: boolean
  unique_visitors_30d?: number
  visit_count_30d?: number
  owner_name?: string | null
  owner_email?: string | null
  is_favorited?: boolean
  my_permission?: string | null
  access_level?: string
  is_template?: boolean
  storage_used_bytes?: number
  storage_limit_bytes?: number
  storage_percent?: number
  storage_over_limit?: boolean
}

interface CardProps<T extends BrowseCardApp> {
  app: T
  catCfg?: CategoryStyle | null
  onToggleFavorite?: (app: T) => void
  badges?: ReactNode
  bottomBadges?: ReactNode
  actions?: ReactNode
  menu?: ReactNode
  topRowVariant?: TopRowVariant
  showStorageBadge?: boolean
}

export function BrowseGridCard<T extends BrowseCardApp>({
  app, catCfg, onToggleFavorite, badges, bottomBadges, actions, menu, topRowVariant = 'status-badge', showStorageBadge = true,
}: CardProps<T>) {
  const navigate = useNavigate()

  const isPopular = !app.is_featured && (app.unique_visitors_30d || 0) >= 3
  const statusInfo = statusConfig[app.status] || statusConfig.stopped
  const hasAccess = app.my_permission != null
  const storageBadge = showStorageBadge ? <StorageBadge app={app} /> : null

  return (
    <div
      className="p-3.5 rounded-lg border border-border bg-card transition-colors flex flex-col hover:border-primary/40 cursor-pointer"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        if (app.status === 'error' || !app.url) {
          navigate(`/apps/${app.name}`)
        } else {
          window.open(app.url, '_blank', 'noopener,noreferrer')
        }
      }}
    >
      {/* Top row: variant-dependent content | actions */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          {topRowVariant === 'status-badge' ? (
            <StatusBadge status={app.status} />
          ) : topRowVariant === 'creator' ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground truncate">
              <User className="w-3 h-3 shrink-0" />
              {app.owner_name || app.owner_email || 'Unknown'}
            </span>
          ) : (
            <>
              <span className="relative w-2 h-2 shrink-0" title={statusInfo.label}>
                <span className={`absolute inset-0 rounded-full ${statusInfo.color}`} />
                {statusInfo.pulse && <span className={`absolute inset-0 rounded-full ${statusInfo.color} animate-ping`} />}
              </span>
              <span className="text-[11px] text-muted-foreground truncate">/a/{app.name}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {onToggleFavorite && hasAccess && (
            <FavoriteButton app={app} onToggle={onToggleFavorite} />
          )}
          {actions}
          {menu}
        </div>
      </div>

      <h3 className="font-semibold text-sm truncate mb-0.5 flex items-center gap-1" title={app.display_name || app.name}>
        {app.is_template && <LayoutTemplate className="w-3.5 h-3.5 text-primary shrink-0" />}
        <span className="truncate">{app.display_name || app.name}</span>
        {app.is_featured && <FeaturedIcon />}
        {isPopular && <PopularIcon />}
      </h3>

      <p className="text-xs text-muted-foreground line-clamp-2 mb-3 flex-1 min-h-[2lh]" title={app.description || undefined}>
        {app.description || 'No description'}
      </p>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="flex items-center gap-2.5 text-xs text-muted-foreground min-w-0 overflow-hidden">
          <AccessLevelIcon accessLevel={app.access_level} />
          <VisitorBadge count={app.visit_count_30d} />
          {storageBadge}
          {app.my_permission === 'owner' && <OwnerBadge />}
          {catCfg && (
            <span className={`text-[11px] ${catCfg.text} truncate`}>#{catCfg.label}</span>
          )}
          {badges}
          {bottomBadges}
        </div>
        <AvatarWithTooltip name={app.owner_name} email={app.owner_email} />
      </div>
    </div>
  )
}

export function BrowseListCard<T extends BrowseCardApp>({
  app, catCfg, onToggleFavorite, badges, bottomBadges, actions, menu, showStorageBadge = true,
}: CardProps<T>) {
  const navigate = useNavigate()

  const isPopular = !app.is_featured && (app.unique_visitors_30d || 0) >= 3
  const statusInfo = statusConfig[app.status] || statusConfig.stopped
  const hasAccess = app.my_permission != null
  const storageBadge = showStorageBadge ? <StorageBadge app={app} /> : null

  return (
    <div
      className="px-3.5 py-2.5 rounded-lg border border-border bg-card transition-colors hover:border-primary/40 cursor-pointer"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        if (app.status === 'error' || !app.url) {
          navigate(`/apps/${app.name}`)
        } else {
          window.open(app.url, '_blank', 'noopener,noreferrer')
        }
      }}
    >
      <div className="flex items-center gap-3">
        {onToggleFavorite && hasAccess && (
          <FavoriteButton app={app} onToggle={onToggleFavorite} />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="relative w-2 h-2 shrink-0" title={statusInfo.label}>
              <span className={`absolute inset-0 rounded-full ${statusInfo.color}`} />
              {statusInfo.pulse && <span className={`absolute inset-0 rounded-full ${statusInfo.color} animate-ping`} />}
            </span>
            {app.is_template && <LayoutTemplate className="w-3.5 h-3.5 text-primary shrink-0" />}
            <h3 className="font-semibold truncate" title={app.display_name || app.name}>
              {app.display_name || app.name}
            </h3>
            {app.is_featured && <FeaturedIcon />}
            {isPopular && <PopularIcon />}
          </div>
          {app.description && (
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5" title={app.description}>
              {app.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <AccessLevelIcon accessLevel={app.access_level} />
          {app.my_permission === 'owner' && <OwnerBadge />}
          {catCfg && (
            <span className={`text-[11px] ${catCfg.text}`}>#{catCfg.label}</span>
          )}
          {badges}
          <VisitorBadge count={app.visit_count_30d} />
          {storageBadge}
          {bottomBadges}
          <AvatarWithTooltip name={app.owner_name} email={app.owner_email} />
          {actions}
          {menu}
        </div>
      </div>
    </div>
  )
}

/* ---- Shared Sub-Components ---- */

const statusTooltips: Record<string, string> = {
  running: 'App is running and accessible to users',
  stopped: 'App is not running. Start it to make it accessible',
  starting: 'App container is being set up and will be available shortly',
  error: 'App encountered an error during deployment. Check logs for details',
}

export function FeaturedIcon() {
  return (
    
      <span title="Featured app -- highlighted by the platform team" className="text-amber-500">
        <BadgeCheck className="w-3.5 h-3.5" />
      </span>
    
  )
}

export function PopularIcon() {
  return (
    
      <span title="Popular app with high usage" className="text-orange-500">
        <Flame className="w-3.5 h-3.5" />
      </span>
    
  )
}

export function OwnerBadge() {
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[11px] text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded"
      title="Your app"
    >
      <Crown className="w-2.5 h-2.5" />
      Owner
    </span>
  )
}

function StorageBadge({ app }: { app: BrowseCardApp }) {
  const used = app.storage_used_bytes || 0
  if (used <= 0) return null
  const limit = app.storage_limit_bytes || 0
  const percent = app.storage_percent || (limit ? (used / limit) * 100 : 0)
  const title = limit
    ? `${formatBytes(used)} of ${formatBytes(limit)} (${percent.toFixed(1)}%)`
    : formatBytes(used)
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] ${
        app.storage_over_limit ? 'text-red-500' : percent >= 75 ? 'text-amber-500' : 'text-muted-foreground'
      }`}
      title={`Disk: ${title}`}
    >
      <HardDrive className="w-3 h-3" />
      {formatBytes(used)}
    </span>
  )
}

export function StatusBadge({ status }: { status: string }) {
  const info = statusConfig[status] || statusConfig.stopped
  return (
    
      <span title={statusTooltips[status] || 'Unknown status'} className={`inline-flex items-center gap-1 text-[11px] ${
        status === 'running' ? 'text-green-500' :
        status === 'error' ? 'text-red-500' :
        'text-muted-foreground'
      }`}>
        <span className="relative w-1.5 h-1.5">
          <span className={`absolute inset-0 rounded-full ${info.color}`} />
          {info.pulse && <span className={`absolute inset-0 rounded-full ${info.color} animate-ping`} />}
        </span>
        {info.label}
      </span>
    
  )
}

export function AvatarWithTooltip({ name, email }: { name?: string | null; email?: string | null }) {
  const displayName = name || email || '?'
  const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
  const tooltipText = name ? `${name}${email ? `\n${email}` : ''}` : email || ''

  return (
    
      <div title={tooltipText}
        className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0"
      >
        <span className="text-[10px] font-medium text-muted-foreground">{initials}</span>
      </div>
    
  )
}

export function FavoriteButton<T extends { is_favorited?: boolean }>(
  { app, onToggle }: { app: T; onToggle: (app: T) => void },
) {
  return (
    
      <button title={app.is_favorited ? 'Remove from favorites' : 'Add to favorites'}
        onClick={(e) => { e.stopPropagation(); onToggle(app) }}
        className={`p-1.5 rounded transition-colors ${
          app.is_favorited
            ? 'text-yellow-500 hover:text-yellow-600'
            : 'text-muted-foreground/40 hover:text-yellow-500'
        }`}
      >
        <Star className="w-4 h-4" fill={app.is_favorited ? 'currentColor' : 'none'} />
      </button>
    
  )
}

const accessLevelConfig: Record<string, { icon: typeof Lock; label: string }> = {
  personal: { icon: Lock, label: 'Private' },
  specific: { icon: UserCheck, label: 'Shared' },
  link: { icon: Link2, label: 'Link' },
  platform: { icon: Users, label: 'Platform' },
}

export function AccessLevelIcon({ accessLevel }: { accessLevel?: string }) {
  const cfg = accessLevelConfig[accessLevel as keyof typeof accessLevelConfig] || accessLevelConfig.personal
  const Icon = cfg.icon
  return (
    <span className="flex items-center text-muted-foreground shrink-0" title={cfg.label}>
      <Icon className="w-3 h-3" />
    </span>
  )
}

export function PermissionBadge({ permission }: { permission?: string }) {
  if (permission === 'owner') {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[11px] text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded"
        title="Owner"
      >
        <Crown className="w-2.5 h-2.5" />
        Owner
      </span>
    )
  }
  if (permission === 'edit' || permission === 'admin') {
    return (
      
        <span title="You can edit this app's code and settings"
          className="inline-flex items-center gap-0.5 text-[11px] text-blue-600 bg-blue-500/10 px-1.5 py-0.5 rounded"
        >
          <Pencil className="w-2.5 h-2.5" />
          Editor
        </span>
      
    )
  }
  return (
    
      <span title="You can view this app but cannot make changes"
        className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded"
      >
        <Eye className="w-2.5 h-2.5" />
        View Only
      </span>
    
  )
}
